import React from 'react';
import styles from './Badge.module.scss';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className = '',
}) => {
  return (
    <span className={`${styles.badge} ${styles[variant]} ${styles[size]} ${className}`}>
      {children}
    </span>
  );
};

// Pre-configured badges for transaction types
export const TransactionTypeBadge: React.FC<{ type: 'earn' | 'redeem' | 'adjustment' }> = ({ type }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    earn: { label: 'EARN', variant: 'success' },
    redeem: { label: 'REDEEM', variant: 'info' },
    adjustment: { label: 'ADJUSTMENT', variant: 'warning' },
  };
  
  const { label, variant } = config[type] || { label: type.toUpperCase(), variant: 'default' };
  return <Badge variant={variant}>{label}</Badge>;
};

// Pre-configured badges for source systems
export const SourceSystemBadge: React.FC<{ source: 'explorations' | 'admin' | 'rewards' }> = ({ source }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    explorations: { label: 'Explorations', variant: 'default' },
    admin: { label: 'Admin', variant: 'warning' },
    rewards: { label: 'Rewards', variant: 'info' },
  };
  
  const { label, variant } = config[source] || { label: source, variant: 'default' };
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// Pre-configured badges for reward categories
export const RewardCategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    'cgm': { label: 'CGM', variant: 'info' },
    'glp-1': { label: 'GLP-1', variant: 'warning' },
    'reward cards': { label: 'Reward Cards', variant: 'success' },
  };
  
  const { label, variant } = config[category] || { label: category, variant: 'default' };
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// Pre-configured badges for reward status
export const RewardStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    'ACTIVE': { label: 'Active', variant: 'success' },
    'DRAFT': { label: 'Draft', variant: 'default' },
    'ARCHIVED': { label: 'Archived', variant: 'danger' },
  };
  
  const { label, variant } = config[status] || { label: status, variant: 'default' };
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// Pre-configured badges for reward variants
export const RewardVariantBadge: React.FC<{ variant: string }> = ({ variant: rewardVariant }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    'default': { label: 'Default', variant: 'default' },
    'everyday': { label: 'Everyday', variant: 'info' },
    'healthy-living': { label: 'Healthy Living', variant: 'success' },
  };
  
  const { label, variant } = config[rewardVariant] || { label: rewardVariant, variant: 'default' };
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// Pre-configured badges for partner types
export const PartnerTypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    'ACO': { label: 'ACO', variant: 'info' },
    'EMPLOYER': { label: 'Employer', variant: 'default' },
    'MA': { label: 'Medicare Advantage', variant: 'success' },
  };
  
  const { label, variant } = config[type] || { label: type, variant: 'default' };
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// Pre-configured badges for partner status (active/inactive)
export const PartnerStatusBadge: React.FC<{
  isActive: boolean;
  scheduledActivationAt?: string | null;
}> = ({ isActive, scheduledActivationAt }) => {
  if (isActive) {
    return <Badge variant="success" size="sm">Active</Badge>;
  }
  if (scheduledActivationAt) {
    return <Badge variant="warning" size="sm">Scheduled</Badge>;
  }
  return <Badge variant="danger" size="sm">Inactive</Badge>;
};

// Pre-configured badges for offer assignment strategy
// Only MIGRATION and MANUAL exist in real DB
export const AssignmentStrategyBadge: React.FC<{ strategy: string }> = ({ strategy }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    'MIGRATION': { label: 'Migration', variant: 'default' },
    'MANUAL': { label: 'Manual', variant: 'warning' },
  };
  
  const { label, variant } = config[strategy] || { label: strategy, variant: 'default' };
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// Pre-configured badges for fulfillment status
// isActionable: When false for FAILED status, shows dimmed non-actionable indicator
export const FulfillmentStatusBadge: React.FC<{ 
  status: string; 
  isActionable?: boolean;
}> = ({ status, isActionable = true }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    'PENDING': { label: 'Pending', variant: 'warning' },
    'COMPLETED': { label: 'Completed', variant: 'success' },
    'FAILED': { label: 'Failed', variant: 'danger' },
  };
  
  const { label, variant } = config[status] || { label: status, variant: 'default' };
  
  // For non-actionable failed fulfillments, show dimmed version
  if (status === 'FAILED' && !isActionable) {
    return (
      <Badge variant="default" size="sm" className={styles.nonActionable}>
        {label}
      </Badge>
    );
  }
  
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// Pre-configured badges for dashboard activity types
export const ActivityTypeBadge: React.FC<{ type: 'transaction' | 'fulfillment' }> = ({ type }) => {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    'transaction': { label: 'Transaction', variant: 'info' },
    'fulfillment': { label: 'Fulfillment', variant: 'warning' },
  };
  
  const { label, variant } = config[type] || { label: type, variant: 'default' };
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

export default Badge;

